/**
 * Project Service Groups + Line Items API.
 */

import { apiFetch } from './fetch';
import { API } from '../constants';

// ─── Service Groups ─────────────────────────────────────────────────────────

export type ServiceGroupType = 'standard' | 'rush';

export interface ServiceGroupResponse {
    id: string;
    projectId: string;
    serviceId?: string;
    serviceTypeName?: string;
    languageId?: string;
    languageName?: string;
    type?: ServiceGroupType;
    /**
     * Legacy marker for overage groups created before DUBVM-128. New groups
     * are never overage; the per-line `LineItemResponse.isOverage` is what
     * drives the overage figures. Still part of the FX-lock key and the
     * export column label for those groups.
     */
    isOverage?: boolean;
    totalCost: number;
    totalInvoiceAmount: number;
    marginPct: number;
    /**
     * This group's share of the project PM Fee (DUBVM-106): the PM-Fee-flagged
     * approved lines' totalInvoiceAmount × the project's pmFeeMarkup. Derived by
     * the API on every read from the same population as the three totals above;
     * counted in marginPct but in neither total.
     *
     * Optional for the same reason `LineItemResponse.description` is: the web
     * app and the API deploy separately, and until the API carrying this field
     * is out the response has no `pmFee` at all. Readers fall back to the
     * client-side `calcGroupTotals(...).pmFee`, which is the same formula.
     */
    pmFee?: number;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
}

export interface CreateServiceGroupRequest {
    projectId: string;
    serviceId?: string;
    serviceTypeName?: string;
    languageId?: string;
    languageName?: string;
    type?: ServiceGroupType;
}

/** List service groups for a project. */
export async function listServiceGroups(projectId: string): Promise<ServiceGroupResponse[]> {
    const result = await apiFetch<ServiceGroupResponse[] | null>(`${API.PROJECTS}/${projectId}/service-groups`);
    return Array.isArray(result) ? result : [];
}

/** Create a new service group. */
export async function createServiceGroup(payload: CreateServiceGroupRequest): Promise<ServiceGroupResponse> {
    return apiFetch<ServiceGroupResponse>(API.SERVICE_GROUPS, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export interface UpdateServiceGroupRequest {
    serviceTypeName?: string;
    languageName?: string;
    totalCost?: number;
    totalInvoiceAmount?: number;
    marginPct?: number;
}

/** Update a service group. */
export async function updateServiceGroup(
    id: string,
    payload: UpdateServiceGroupRequest,
): Promise<ServiceGroupResponse> {
    return apiFetch<ServiceGroupResponse>(`${API.SERVICE_GROUPS}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
}

/** Delete a service group. */
export async function deleteServiceGroup(id: string): Promise<void> {
    await apiFetch(`${API.SERVICE_GROUPS}/${id}`, { method: 'DELETE' });
}

// ─── Line Items ─────────────────────────────────────────────────────────────

export interface LineItemResponse {
    id: string;
    displayId?: string;
    serviceGroupId: string;
    // Optional: imported line items have no sub-service, and the backend omits
    // empty strings (json omitempty), so this is absent for those rows.
    subServiceName?: string;
    vendorId?: string;
    vendorName?: string;
    currencyCode: string;
    currencyConversionRate: number;
    unitType: string;
    quantity: number;
    costLocalCurrency: number;
    costInvoiceCurrency: number;
    totalCost: number;
    rateCard: number;
    markup: number;
    rateInvoiceCurrency: number;
    totalInvoiceAmount: number;
    /**
     * (rateInvoiceCurrency − rateCard) × quantity × (1 + project pmFeeMarkup/100),
     * signed; 0 when the client rate is the rate card (DUBVM-102). Sent on the
     * service-group reads only, and omitted when the API could not load the
     * project's markup — and absent from an API that predates the field. Readers
     * fall back to `calcTotalVariance`, the same formula, and show it through
     * `totalVarianceDisplay`.
     */
    totalVariance?: number;
    status: string;
    /**
     * Counts the line in the project's overage roll-up (DUBVM-128). Seeded from
     * the sub-service's Admin default when the line is created and owned by the
     * backsheet from then on. Optional only for deploy skew, like `description`.
     */
    isOverage?: boolean;
    /**
     * Optional free-text context for the sub-service (DUBVM-70).
     *
     * A current server always sends it — '' rather than omitted — so the view can
     * render the blank state without distinguishing absent from empty. Typed
     * optional anyway because the web app and the API deploy separately: until the
     * API carrying this field is out, responses have no `description` at all, and a
     * required type would be a lie the callers then have to handle unguarded.
     */
    description?: string;
    // DUBVM-53 per-sub-service toggles.
    minMargin10Pct?: boolean;
    pmFeeApplicable?: boolean;
    includeInInvoice?: boolean;
    isActive: boolean;
    createdAt: string;
    createdBy: string;
}

export interface CreateLineItemRequest {
    serviceGroupId: string;
    subServiceName?: string;
    vendorId?: string;
    vendorName?: string;
    currencyCode: string;
    currencyConversionRate?: number;
    unitType: string;
    quantity: number;
    costLocalCurrency: number;
    costInvoiceCurrency: number;
    totalCost: number;
    rateCard: number;
    markup: number;
    rateInvoiceCurrency: number;
    totalInvoiceAmount: number;
    status?: string;
    /**
     * Omitted lets the API seed it from the sub-service's Admin default; the
     * dialog always sends the checkbox value so what the user saw is what is saved.
     */
    isOverage?: boolean;
    /**
     * Optional free-text context (DUBVM-70). Sent on every save, including as ''
     * — the dialog holds the field's full state, so an empty box must clear a
     * previously saved value rather than leave it in place.
     */
    description?: string;
    minMargin10Pct?: boolean;
    pmFeeApplicable?: boolean;
    includeInInvoice?: boolean;
}

/** List line items for a service group. */
export async function listLineItems(serviceGroupId: string): Promise<LineItemResponse[]> {
    if (!serviceGroupId) {
        return [];
    }
    const result = await apiFetch<LineItemResponse[] | null>(`${API.LINE_ITEMS}?serviceGroupId=${serviceGroupId}`);
    return Array.isArray(result) ? result : [];
}

/** Create a new line item. */
export async function createLineItem(payload: CreateLineItemRequest): Promise<LineItemResponse> {
    return apiFetch<LineItemResponse>(API.LINE_ITEMS, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

/**
 * Update a line item.
 *
 * Only the fields present in `payload` are applied — the backend leaves anything
 * omitted at its stored value, so a partial update is safe.
 */
export async function updateLineItem(
    id: string,
    payload: Partial<CreateLineItemRequest>,
    signal?: AbortSignal,
): Promise<LineItemResponse> {
    const opts: RequestInit = {
        method: 'PUT',
        body: JSON.stringify(payload),
    };
    if (signal) {
        opts.signal = signal;
    }
    return apiFetch<LineItemResponse>(`${API.LINE_ITEMS}/${id}`, opts);
}

/** Delete a line item. */
export async function deleteLineItem(id: string): Promise<void> {
    await apiFetch(`${API.LINE_ITEMS}/${id}`, { method: 'DELETE' });
}
